import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { meetingService, Meeting } from '../services/meetingService';
import { useDialog } from '../context/DialogContext';
import { t } from '../i18n/translate';
import styles from '../styles/meetingsTabContent.module.css';

const DAY_ABBREV: Record<string, string> = {
  Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu',
  Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun',
};

/** For workshops: derive schedule_day from workshop_first_session_date (YYYY-MM-DD) for display. */
function displayScheduleDay(m: Meeting): string {
  if (m.schedule_day) return m.schedule_day;
  const dateStr = m.workshop_first_session_date;
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return '';
  try {
    const d = new Date(dateStr + 'T12:00:00');
    const day = d.toLocaleDateString('en-US', { weekday: 'long' });
    return day || '';
  } catch {
    return '';
  }
}

const FORMAT_LABELS: Record<string, string> = {
  online: 'Online',
  hybrid: 'Hybrid',
  in_person: 'In-Person',
  telemeeting: 'Telemeeting',
};

const FORMAT_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'online', label: 'Online' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'in_person', label: 'In-Person' },
  { value: 'telemeeting', label: 'Telemeeting' },
];

const DESCRIPTION_PREVIEW_MAX = 40;

/** First line of description, max 40 chars; stop at newline. If truncated or multi-line, add "…". Full text for tooltip. */
function descriptionPreview(description: string | undefined): { display: string; full: string } {
  const full = (description || '').trim();
  const firstLine = full.split('\n')[0]?.trim() ?? '';
  const truncated = firstLine.length > DESCRIPTION_PREVIEW_MAX;
  const display = truncated
    ? firstLine.slice(0, DESCRIPTION_PREVIEW_MAX) + '…'
    : full !== firstLine
      ? firstLine + ' …'
      : firstLine;
  return { display: display || '—', full: full || '' };
}

function formatTime(s: string | undefined): string {
  if (!s || !/^\d{1,2}:\d{2}$/.test(s)) return '—';
  const [h, m] = s.split(':').map(Number);
  const hour = h % 12 || 12;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function formatTzAbbrev(tz: string | undefined): string {
  if (!tz) return '';
  const parts = tz.split('/');
  const last = parts[parts.length - 1];
  const known: Record<string, string> = {
    'Los_Angeles': 'PT', 'New_York': 'ET', 'Chicago': 'CT', 'Denver': 'MT',
    'Phoenix': 'MST', 'Anchorage': 'AKT', 'Honolulu': 'HST', 'London': 'GMT',
    'Paris': 'CET', 'Berlin': 'CET', 'Tokyo': 'JST', 'Sydney': 'AEST',
  };
  return known[last] || last.replace('_', ' ').slice(0, 3);
}

function formatFormatLocation(m: Meeting): string {
  const fmt = (m.format || 'online').toLowerCase();
  const label = FORMAT_LABELS[fmt] || fmt;
  if (fmt === 'telemeeting') return label;
  if (m.location?.trim()) return `${label} (${m.location.trim()})`;
  return label;
}

/** Tagline from API (supports both tagline and tag_line for backend compatibility). */
function getTagline(m: Meeting): string {
  return ((m.tagline ?? m.tag_line) || '').trim();
}

const OFFICIAL_FINDER_URL = 'https://www.saa-meetings.org/';
const OFFICIAL_FINDER_TOKENS = [
  "SAA's official meeting finder",
  'SAA’s official meeting finder',
];

function renderHeaderWithOptionalFinderLink(text: string): React.ReactNode {
  const finderToken = OFFICIAL_FINDER_TOKENS.find((t) => text.includes(t));
  if (!finderToken) return text;

  const parts = text.split(finderToken);
  if (parts.length < 2) return text;

  return (
    <>
      {parts[0]}
      <a
        href={OFFICIAL_FINDER_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        {finderToken}
      </a>
      {parts.slice(1).join(finderToken)}
    </>
  );
}

function renderTwoLineHeader(text: string): React.ReactNode {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  const firstLine = lines[0] ?? '';
  const secondLine = lines[1] ?? null;

  if (!secondLine) {
    return <strong>{firstLine}</strong>;
  }

  return (
    <>
      <strong>{firstLine}</strong>
      <br />
      <span className={styles.headerSubtext}>
        <em>{renderHeaderWithOptionalFinderLink(secondLine)}</em>
      </span>
    </>
  );
}

function firstColumnLabel(m: Meeting): string {
  const name = (m.name || '').trim();
  if (name) return name;
  const t = getTagline(m);
  if (t) return t;
  const fmt = (m.format || 'online').toLowerCase();
  return FORMAT_LABELS[fmt] || fmt;
}

interface MeetingsTabContentProps {
  user?: any;
  onLogin: () => void;
  headerText?: string;
}

const MeetingsTabContent: React.FC<MeetingsTabContentProps> = ({ user, onLogin, headerText }) => {
  const navigate = useNavigate();
  const { showConfirm } = useDialog();
  const [homeMeetings, setHomeMeetings] = useState<Meeting[]>([]);
  const [allMeetings, setAllMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterFormat, setFilterFormat] = useState('');
  const [filterDay, setFilterDay] = useState('');

  useEffect(() => {
    loadMeetings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadMeetings = async () => {
    setLoading(true);
    try {
      const [home, list] = await Promise.all([
        user ? meetingService.getHomeMeetings() : Promise.resolve([]),
        meetingService.listMeetings('saa'),
      ]);
      setHomeMeetings(Array.isArray(home) ? home : []);
      setAllMeetings(list || []);
    } catch (err) {
      console.error('Error loading meetings:', err);
      setAllMeetings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromHome = (code: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    showConfirm({
      title: t('meetings.dialog.remove_home_title'),
      message: t('meetings.dialog.remove_home_message'),
      confirmText: t('meetings.dialog.remove'),
      cancelText: t('dialog.cancel'),
      variant: 'danger',
      onConfirm: async () => {
        try {
          await meetingService.removeFromHome(code);
          setHomeMeetings((prev) => prev.filter((m) => m.meeting_code !== code));
        } catch (err) {
          console.error('Error removing from home:', err);
        }
      },
    });
  };

  const homeCodes = useMemo(() => new Set(homeMeetings.map((m) => m.meeting_code)), [homeMeetings]);

  const filteredList = useMemo(() => {
    let list = allMeetings.filter((m) => !homeCodes.has(m.meeting_code));
    if (filterFormat) {
      const f = filterFormat.toLowerCase();
      list = list.filter((m) => (m.format || 'online').toLowerCase() === f);
    }
    if (filterDay) {
      const d = filterDay.toLowerCase();
      list = list.filter((m) => displayScheduleDay(m).toLowerCase() === d);
    }
    return list;
  }, [allMeetings, homeCodes, filterFormat, filterDay]);

  const displayMeetings = useMemo(() => {
    const saved = homeMeetings.slice();
    const todayLower = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const rest = filteredList.filter((m) => !homeCodes.has(m.meeting_code));
    const todayRest = rest.filter((m) => displayScheduleDay(m).toLowerCase() === todayLower);
    const otherRest = rest.filter((m) => displayScheduleDay(m).toLowerCase() !== todayLower);
    otherRest.sort((a, b) =>
      firstColumnLabel(a).localeCompare(firstColumnLabel(b), 'en', { sensitivity: 'base' })
    );
    return [...saved, ...todayRest, ...otherRest];
  }, [homeMeetings, filteredList, homeCodes]);

  const hasActiveFilters = !!filterFormat || !!filterDay;

  const clearFilters = () => {
    setFilterFormat('');
    setFilterDay('');
  };

  const goToMeeting = (code: string) => {
    navigate(`/meetings/${code}`);
  };

  const todayDayLower = useMemo(
    () => new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase(),
    []
  );

  return (
    <div className={styles.meetingsTab}>
      {headerText ? (
        <p className={styles.headerText}>{renderTwoLineHeader(headerText)}</p>
      ) : (
        <>
          <p className={styles.headerText}>
            <strong>Find SAA meeting home pages and join meetings on Recovery Commons.</strong>
            <br />
            <span className={styles.headerSubtext}>
              <em>
                For finding all SAA meetings by time and location, use{' '}
                <a
                  href={OFFICIAL_FINDER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  SAA&apos;s official meeting finder
                </a>
                .
              </em>
            </span>
          </p>
        </>
      )}

      {filterOpen && (
        <div className={styles.filterDialog}>
          <label>
            Format
            <select value={filterFormat} onChange={(e) => setFilterFormat(e.target.value)}>
              {FORMAT_OPTIONS.map((o) => (
                <option key={o.value || 'all'} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label>
            Day
            <select value={filterDay} onChange={(e) => setFilterDay(e.target.value)}>
              <option value="">All</option>
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((d) => (
                <option key={d} value={d.toLowerCase()}>{d}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      {/* Table (desktop) / Cards (mobile) */}
      <section className={styles.listSection}>
        <div className={styles.titleRow}>
          <h3 className={styles.sectionTitle}>SAA Meetings and Workshops</h3>
          <div className={styles.titleActions}>
            <a href="/meetings/create-info?program=saa" className={styles.addHomeLink}>
              Don&apos;t see your meeting? Add a home page
            </a>
            <button
              type="button"
              className={styles.filtersBtn}
              onClick={() => setFilterOpen((o) => !o)}
              aria-expanded={filterOpen}
            >
              Filters {hasActiveFilters ? `(${[filterFormat, filterDay].filter(Boolean).length})` : ''}
            </button>
            {hasActiveFilters && (
              <button type="button" className={styles.clearFiltersBtn} onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        </div>
        {loading ? (
          <p className={styles.loadingHint}>Loading meetings...</p>
        ) : displayMeetings.length === 0 ? (
          <p className={styles.emptyHint}>
            {hasActiveFilters ? 'No meetings match the current filters.' : 'No meetings found. Create one to get started.'}
          </p>
        ) : (
          <>
            {/* Desktop: table */}
            <div className={styles.tableWrap}>
              <table className={styles.meetingsTable}>
                <thead>
                  <tr>
                    <th className={styles.colFirst}></th>
                    <th>Day</th>
                    <th>Time / TZ</th>
                    <th>Format / Location</th>
                    <th>Description</th>
                    <th className={styles.colCode}>ID</th>
                    {user && <th className={styles.colRemove} aria-label="Remove from list" />}
                  </tr>
                </thead>
                <tbody>
                  {displayMeetings.map((m) => {
                    const isSaved = homeCodes.has(m.meeting_code);
                    const isToday =
                      !isSaved && displayScheduleDay(m).toLowerCase() === todayDayLower;
                    const rowClass = isSaved
                      ? `${styles.tableRow} ${styles.tableRowSaved}`
                      : isToday
                        ? `${styles.tableRow} ${styles.tableRowToday}`
                        : styles.tableRow;
                    return (
                      <tr
                        key={m.meeting_code}
                        role="button"
                        tabIndex={0}
                        className={rowClass}
                        onClick={() => goToMeeting(m.meeting_code)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            goToMeeting(m.meeting_code);
                          }
                        }}
                      >
                        <td className={styles.colFirst}>
                          {isSaved && <span className={styles.myMeetingLabel}>My meeting</span>}
                          {isToday && <span className={styles.todayLabel}>Today</span>}
                          <span className={styles.cellLink}>
                            {firstColumnLabel(m)}
                            {m.kind === 'workshop' && <span className={styles.kindBadgeInline}>W</span>}
                          </span>
                        </td>
                        <td>{displayScheduleDay(m) ? DAY_ABBREV[displayScheduleDay(m)] || displayScheduleDay(m).slice(0, 3) : '—'}</td>
                        <td>
                          {formatTime(m.schedule_start_time)}
                          {m.timezone ? ` ${formatTzAbbrev(m.timezone)}` : ''}
                        </td>
                        <td>{formatFormatLocation(m)}</td>
                        <td
                          className={styles.colDescription}
                          title={descriptionPreview(m.description).full || undefined}
                        >
                          {descriptionPreview(m.description).display}
                        </td>
                        <td className={styles.colCode}>{m.meeting_code}</td>
                        {user && (
                          <td className={styles.colRemove} onClick={(e) => e.stopPropagation()}>
                            {isSaved ? (
                              <button
                                type="button"
                                className={styles.removeBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveFromHome(m.meeting_code, e);
                                }}
                                title="Remove from my list"
                                aria-label="Remove from my list"
                              >
                                ✕
                              </button>
                            ) : null}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile: cards */}
            <ul className={styles.cardsList}>
              {displayMeetings.map((m) => {
                const isSaved = homeCodes.has(m.meeting_code);
                const isToday =
                  !isSaved && displayScheduleDay(m).toLowerCase() === todayDayLower;
                const cardClass = isSaved
                  ? `${styles.card} ${styles.cardSaved}`
                  : isToday
                    ? `${styles.card} ${styles.cardToday}`
                    : styles.card;
                return (
                  <li key={m.meeting_code} className={cardClass}>
                    <a
                      href={`/meetings/${m.meeting_code}`}
                      className={styles.cardLink}
                      onClick={(e) => {
                        e.preventDefault();
                        goToMeeting(m.meeting_code);
                      }}
                    >
                      {isSaved && <span className={styles.myMeetingLabel}>My meeting</span>}
                      {isToday && <span className={styles.todayLabel}>Today</span>}
                      <span className={styles.cardTitle}>
                        {firstColumnLabel(m)}
                        {m.kind === 'workshop' && <span className={styles.kindBadgeInline}>W</span>}
                      </span>
                      <span className={styles.cardCode}>{m.meeting_code}</span>
                    <span className={styles.cardMeta}>
                      {displayScheduleDay(m) ? DAY_ABBREV[displayScheduleDay(m)] : '—'} · {formatTime(m.schedule_start_time)}
                      {m.timezone ? ` ${formatTzAbbrev(m.timezone)}` : ''}
                    </span>
                    <span className={styles.cardMeta}>
                      {formatFormatLocation(m)}
                    </span>
                    {(m.description || '').trim() && (
                      <span
                        className={styles.cardDescription}
                        title={descriptionPreview(m.description).full || undefined}
                      >
                        {descriptionPreview(m.description).display}
                      </span>
                    )}
                  </a>
                  {user && homeCodes.has(m.meeting_code) && (
                    <button
                      type="button"
                      className={styles.removeBtnCard}
                      onClick={(e) => handleRemoveFromHome(m.meeting_code, e)}
                      title="Remove from my list"
                      aria-label="Remove from my list"
                    >
                      ✕
                    </button>
                  )}
                </li>
                );
              })}
            </ul>
            {displayMeetings.some((m) => m.kind === 'workshop') && (
              <p className={styles.mwLegend}>W = Workshop</p>
            )}
          </>
        )}
      </section>

      {!user && (
        <p className={styles.signInHint}>
          <button type="button" onClick={onLogin} className={styles.signInLink}>
            Sign in
          </button>{' '}
          to add meetings to My Meetings and create your own home pages.
        </p>
      )}
    </div>
  );
};

export default MeetingsTabContent;
